import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '../api/client';
import type { User } from '../api/types';
import { clearThemeHint } from '../preferences/theme';

interface AuthState {
  me: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  // First-run: complete the one-time "choose a plan" step and update `me` in place
  // (so the landing resolver stops routing to /plans). Idempotent server-side.
  markOnboarded: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch (err) {
      // 401 simply means "not signed in"; anything else also leaves us logged
      // out for safety. Never surface the error here — guards handle redirects.
      if (!(err instanceof ApiError)) throw err;
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Bootstrap the session once on mount.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const user = await api.login({ email, password });
    setMe(user);
    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Drop the theme hint so the next (possibly different) user never boots
      // into the previous user's saved theme. PreferencesProvider also reverts
      // to defaults when `me` clears.
      clearThemeHint();
      setMe(null);
    }
  }, []);

  const markOnboarded = useCallback(async () => {
    const user = await api.completeOnboarding();
    setMe(user);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ me, loading, login, logout, refresh, markOnboarded }),
    [me, loading, login, logout, refresh, markOnboarded],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
